import React, { useRef, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { streams } from '../utils/streamUtils';
import { RecorderState } from '../../store';

interface CameraPreviewProps {
  fullSize?: boolean;
  size?: string;
}

const CameraPreview: React.FC<CameraPreviewProps> = ({
  fullSize = false,
  size = '6rem',
}) => {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const { mediaStreams, settings } = useSelector(
    (state: RecorderState) => state.recorder,
  );
  const { status } = mediaStreams.userMedia;
  const { webCam } = settings.videoSettings;

  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.srcObject = streams.userMediaStream;
      videoRef.current.muted = true;
      videoRef.current.play();
    }
  }, [mediaStreams.userMedia]);

  if (!webCam) {
    return (
      <div
        className={cn(fullSize ? 'w-full h-auto' : '')}
        style={!fullSize ? { width: size, height: size } : undefined}
      />
    );
  }

  if (status === 'pending') {
    return (
      <Skeleton
        className={cn(
          fullSize ? 'w-full h-0 pb-[56.25%] rounded-xl' : 'rounded-full',
        )}
        style={!fullSize ? { width: size, height: size } : undefined}
      />
    );
  }

  if (fullSize) {
    return (
      <div className="w-full relative pb-[56.25%] rounded-xl overflow-hidden">
        <video
          ref={videoRef}
          muted
          className="absolute top-0 left-0 w-full h-full object-cover scale-x-[-1] rounded-xl"
        />
      </div>
    );
  }

  return (
    <div className="relative aspect-square rounded-full overflow-hidden flex justify-center items-center">
      <video
        ref={videoRef}
        muted
        className="w-full h-full object-cover scale-x-[-1]"
      />
    </div>
  );
};

export default CameraPreview;
